import { isEqual } from "lodash";
import Guid from "_shared/guids/guid";
import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import NotificationBatchSchema from "notifications/_schemas/notification-batch-schema";
import NotificationSchema from "notifications/_schemas/notification-schema";
import {
  addNotification,
  addNotifications,
  removeNotification,
} from "notifications/_stores/notifications-redux-slice";
import Session from "users/__session/session";
import Notification from "notifications/notification";
import MessageNotification from "notifications/message-notifications/message-notification";
import MessageNotificationSchema from "notifications/message-notifications/_schemas/message-notification-schema";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";

const COMMUNICATIONS_INTERFACE_NAME = "communicationsPage";

const toNotification = (payload: unknown): Notification | MessageNotification | undefined => {
  // Try the more specific schema first
  if (MessageNotificationSchema.isValidJSON(payload)) {
    return MessageNotification.fromJSON(payload);
  }

  if (NotificationSchema.isValidJSON(payload)) {
    return Notification.fromJSON(payload);
  }

  return undefined;
};

const getVisibleSenderName = (notification: Notification | MessageNotification): string | null => {
  if ("messageInfo" in notification) {
    return notification.messageInfo?.senderName?.value ?? notification.senderName?.value ?? null;
  }

  return notification.senderName?.value ?? null;
};

const getVisibleSeenState = (notification: Notification | MessageNotification, userId?: Guid) => {
  if (!userId) {
    return {
      isSeen: false,
      seenAt: null,
    };
  }

  if (notification.senderUserId?.isEqualTo(userId)) {
    return {
      isSeen: true,
      seenAt: notification.createdDate?.toISOString?.() ?? null,
    };
  }

  const recipient = notification.recipients.find(({ userId: recipientUserId }) => recipientUserId.isEqualTo(userId));
  const seenAt = recipient?.seenAt?.toISOString?.() ?? null;

  return {
    isSeen: recipient?.isSeen ?? seenAt !== null,
    seenAt,
  };
};

const toVisibleNotificationProjection = (notification: Notification | MessageNotification, userId?: Guid) => {
  const senderUserId = notification.senderUserId?.value ?? null;
  const recipientIds = notification.recipients.map(({ userId: recipientUserId }) => recipientUserId.value);
  const isRecipient = !!userId && notification.recipients.some(({ userId: recipientUserId }) => recipientUserId.isEqualTo(userId));
  const seenState = getVisibleSeenState(notification, userId);

  return {
    id: notification.id.value,
    topicCategory: notification.topic?.category ?? null,
    actionRequired: notification.actionRequired ?? null,
    message: notification.message?.value ?? null,
    // An aggregation reuses the notification the recipient already holds (#830): same id, same
    // sender, same forum — only the count, the message text and the date move. Leaving the count out
    // of the projection would make a second message in a thread that repeats itself look like a
    // change to nothing at all, and the bell would never say "2 Messages from ...".
    occurrenceCount: notification.occurrenceCount ?? null,
    createdDate: notification.createdDate?.toISOString?.() ?? null,
    senderUserId,
    senderName: getVisibleSenderName(notification),
    recipientIds,
    recipientName: notification.recipientName?.value ?? null,
    userRole: userId ? (senderUserId === userId.value ? "sender" : isRecipient ? "recipient" : "other") : "unknown",
    isSeen: seenState.isSeen,
    seenAt: seenState.seenAt,
  };
};

const toNotificationIssuePlaceholder = (entry: unknown): Notification | MessageNotification | undefined => {
  if (!entry || typeof entry !== "object" || !("issue" in entry)) {
    return undefined;
  }

  const notificationId = getNotificationIdValue((entry as { id?: unknown }).id);
  if (!notificationId) {
    return undefined;
  }

  return {
    id: { value: notificationId },
  } as Notification | MessageNotification;
};

const getNotificationFromResponseEntry = (entry: unknown): Notification | MessageNotification | undefined => {
  if (!entry || typeof entry !== "object") {
    return undefined;
  }

  const notification = toNotification(entry);
  if (notification) {
    return notification;
  }

  return toNotificationIssuePlaceholder(entry);
};

const getNotificationIdValue = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "value" in value) {
    const candidate = (value as { value?: unknown }).value;
    return typeof candidate === "string" ? candidate : undefined;
  }

  return undefined;
};

type NotificationsRealtimeState = {
  notifications: {
    byId: {
      entries: Record<string, unknown>;
    };
    interfaceQueries?: {
      lastSuccessfulResponses?: Record<string, { data?: unknown[] }>;
    };
  };
};

const getCurrentCommunicationsNotification = (
  state: {
    notifications: {
      interfaceQueries?: {
        lastSuccessfulResponses?: Record<string, { data?: unknown[] }>;
      };
    };
  },
  notificationId: string,
): Notification | MessageNotification | undefined => {
  const response = state.notifications.interfaceQueries?.lastSuccessfulResponses?.[COMMUNICATIONS_INTERFACE_NAME];
  const matchingEntry = response?.data?.find((entry) => {
    if (!entry || typeof entry !== "object" || !("id" in entry)) {
      return false;
    }

    return getNotificationIdValue((entry as { id?: unknown }).id) === notificationId;
  });

  return getNotificationFromResponseEntry(matchingEntry);
};

/**
 * Whether an update the server announced changes anything this browser is showing. An update to a
 * notification the browser has never loaded is nothing to it, and an update whose visible fields all
 * match what it already holds is a render for no reason.
 */
const isVisibleUpdate = (
  state: NotificationsRealtimeState,
  notification: Notification | MessageNotification,
  currentUserId?: Guid,
): boolean => {
  const currentCommunicationsNotification = getCurrentCommunicationsNotification(state, notification.id.value);
  const existingNotificationJson = state.notifications.byId.entries[notification.id.value];
  const existingNotification = existingNotificationJson
    ? toNotification(existingNotificationJson)
    : currentCommunicationsNotification;

  if (!currentCommunicationsNotification && !existingNotification) {
    return false;
  }

  if (currentCommunicationsNotification) {
    return true;
  }

  return !(
    existingNotification &&
    isEqual(
      toVisibleNotificationProjection(existingNotification, currentUserId),
      toVisibleNotificationProjection(notification, currentUserId),
    )
  );
};

const callbacks = withCallbacks()
  .add("notification-received", (payload: unknown) => (dispatch) => {
    const notification = toNotification(payload);
    if (notification) {
      dispatch(addNotification(notification));
    }
  })
  .add("notification-updated", (payload: unknown) => (dispatch, getState) => {
    const notification = toNotification(payload);
    if (!notification) return;

    const currentUserId = Session.loadFromStorage(() => {}).user?.id;
    const state = getState() as NotificationsRealtimeState;
    if (!isVisibleUpdate(state, notification, currentUserId)) return;

    dispatch(addNotification(notification));
  })
  /**
   * The whole of one bulk change, in one frame and one store update (GitHub issue #2963).
   *
   * Marking a screenful of notifications seen used to arrive as one `notification-updated` per row,
   * so the browser re-rendered once per row. Every notification in the batch is now folded into a
   * single `addNotifications` dispatch, which writes them all and clears the query cache once.
   *
   * Entries are parsed one at a time, so an entry this bundle cannot read costs that entry rather
   * than the batch. The RTK Query by-id caches are deliberately left alone, exactly as the
   * single-notification event leaves them: the acting browser's own mutation already upserts what it
   * received, and every other browser reads through the slice.
   */
  .add("notifications-bulk-updated", (payload: unknown) => (dispatch, getState) => {
    if (!NotificationBatchSchema.isValidJSON(payload)) return;

    const currentUserId = Session.loadFromStorage(() => {}).user?.id;
    const state = getState() as NotificationsRealtimeState;
    const updatedNotifications = payload.notifications
      .map((entry) => toNotification(entry))
      .filter(
        (notification): notification is Notification | MessageNotification =>
          !!notification && isVisibleUpdate(state, notification, currentUserId),
      );

    if (updatedNotifications.length === 0) return;

    dispatch(addNotifications(updatedNotifications));
  })
  .add("notification-deleted", (payload: unknown) => (dispatch) => {
    const deletedNotificationId = new Guid((payload as { id: string }).id);
    dispatch(removeNotification(deletedNotificationId));
  });

export const notificationsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.notifications,
  connectionHealth: { key: "notifications", label: "Notifications" },
  shouldConnectionStartImmediately: true,
});

